import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCompetition } from '../contexts/CompetitionContext';
import { fetchCompetitionLeaderboard } from '../services/competitionApi';
import { PageContainer } from '../components/layout/PageContainer';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoading } from '../components/ui/PageLoading';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { AdminBetScoreModal } from '../components/admin/AdminBetScoreModal';
import { RoundFilterTabs } from '../components/dashboard/UpcomingMatchTabs';
import { useRoundFilter } from '../hooks/useRoundFilter';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  User,
  Target,
  Award,
  CheckCircle,
  AlertCircle,
  Trophy,
  Calendar,
  GamepadIcon,
  Pencil
} from 'lucide-react';
import { formatIsoDateTimeDMY } from '../utils/dateLocale';
import { getTeamDisplayName } from '../utils/teamDisplayName';
import { getPredictionTier } from '../utils/scores';
import { ScoreText } from '../components/ui/ScoreText';

const tierMeta = {
  exact: { Icon: Award, color: 'text-emerald-600', bg: 'border-emerald-200 bg-emerald-50/50' },
  outcome: { Icon: CheckCircle, color: 'text-amber-600', bg: 'border-amber-200 bg-amber-50/50' },
  wrong: { Icon: AlertCircle, color: 'text-red-600', bg: 'border-red-200 bg-red-50/50' },
  pending: { Icon: Target, color: 'text-zinc-500', bg: 'border-zinc-200 bg-zinc-50' }
};

function calculatePoints(predictedResult, actualResult) {
  const tier = getPredictionTier(predictedResult, actualResult);
  if (tier === 'exact') return 3;
  if (tier === 'outcome') return 1;
  return 0;
}

function getBetRound(bet) {
  return bet?.gameId?.round;
}

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { competitionSlug, currentCompetition, basePath } = useCompetition();
  const { t, language } = useLanguage();
  const { user } = useAuth();

  const [userProfile, setUserProfile] = useState(null);
  const [userBets, setUserBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBets: 0,
    totalPoints: 0,
    perfectPredictions: 0,
    correctResults: 0,
    missedPredictions: 0,
    accuracy: 0
  });
  const [editingBet, setEditingBet] = useState(null);

  const {
    view: betsView,
    selectedRound,
    setSelectedRound,
    rounds,
    visibleItems: visibleBets,
    handleViewChange
  } = useRoundFilter(userBets, getBetRound, `${userId}:${competitionSlug}`);

  const fetchUserProfileAndBets = useCallback(async () => {
    try {
      setLoading(true);

      let profileData = null;
      try {
        const profileResponse = await axios.get(`/users/${userId}`);
        profileData = profileResponse.data;
      } catch {
        /* fallback below */
      }

      let betsData = [];
      try {
        const userBetsResponse = await axios.get(`/users/${userId}/bets`);
        betsData = userBetsResponse.data;
        const cid = currentCompetition?._id;
        if (cid) {
          betsData = betsData.filter((b) => {
            if (b.competitionId && String(b.competitionId) === String(cid)) return true;
            const g = b.gameId;
            if (g && g.competitionId && String(g.competitionId) === String(cid)) return true;
            return false;
          });
        }
      } catch {
        betsData = [];
      }

      if (!profileData && betsData.length > 0) {
        const firstBet = betsData[0];
        profileData = {
          _id: userId,
          fullName: firstBet.username || firstBet.user?.fullName || 'Unknown User',
          username: firstBet.username || firstBet.user?.username || 'unknown'
        };
      }

      if (!profileData) {
        try {
          const leaderboardRows = await fetchCompetitionLeaderboard(competitionSlug);
          const userFromLeaderboard = leaderboardRows.find(
            (u) => String(u.userId || u._id) === String(userId)
          );
          if (userFromLeaderboard) {
            profileData = {
              _id: userId,
              username: userFromLeaderboard.username,
              totalPoints: userFromLeaderboard.totalPoints
            };
          }
        } catch {
          /* ignore */
        }
      }

      setUserProfile(profileData);
      setUserBets(betsData);

      const totalBets = betsData.length;
      let totalPoints = 0;
      let perfectPredictions = 0;
      let correctResults = 0;
      let missedPredictions = 0;

      betsData.forEach((bet) => {
        if (bet.gameId?.isFinished && bet.gameId?.finalResult) {
          const points = calculatePoints(bet.predictedResult, bet.gameId.finalResult);
          totalPoints += points;
          if (points === 3) perfectPredictions++;
          else if (points === 1) correctResults++;
          else missedPredictions++;
        }
      });

      const accuracy = totalBets > 0 ? (totalPoints / (totalBets * 3)) * 100 : 0;

      setStats({
        totalBets,
        totalPoints,
        perfectPredictions,
        correctResults,
        missedPredictions,
        accuracy
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error(language === 'en' ? 'Failed to load user data' : 'נכשל בטעינת נתוני המשתמש');
      navigate(`${basePath}/leaderboard`);
    } finally {
      setLoading(false);
    }
  }, [userId, language, navigate, competitionSlug, currentCompetition, basePath]);

  useEffect(() => {
    if (userId) fetchUserProfileAndBets();
  }, [userId, fetchUserProfileAndBets]);

  if (loading) {
    return (
      <PageLoading
        message={language === 'en' ? 'Loading user profile…' : 'טוען פרופיל משתמש…'}
      />
    );
  }

  if (!userProfile) {
    return (
      <PageContainer>
        <Card className="py-16 text-center">
          <p className="text-lg font-semibold text-zinc-900">
            {language === 'en' ? 'User not found' : 'משתמש לא נמצא'}
          </p>
          <Button className="mt-4" onClick={() => navigate(`${basePath}/leaderboard`)}>
            {language === 'en' ? 'Back to leaderboard' : 'חזור לטבלת מובילים'}
          </Button>
        </Card>
      </PageContainer>
    );
  }

  const statCards = [
    {
      icon: Trophy,
      value: stats.totalPoints,
      label: t('totalPoints'),
      accent: 'text-brand-600'
    },
    {
      icon: GamepadIcon,
      value: stats.totalBets,
      label: t('totalBets'),
      accent: 'text-pitch-600'
    },
    {
      icon: Target,
      value: `${stats.accuracy.toFixed(1)}%`,
      label: t('accuracy'),
      accent: 'text-amber-600'
    }
  ];

  return (
    <PageContainer>
      <PageHeader
        title={userProfile.fullName || userProfile.username}
        subtitle={language === 'en' ? 'Player profile' : 'פרופיל שחקן'}
        backTo={`${basePath}/leaderboard`}
        icon={User}
      />

      <div className="mb-8 grid gap-4 sm:grid-cols-3">
        {statCards.map(({ icon: Icon, value, label, accent }) => (
          <Card key={label} className="text-center" padding="p-5">
            <Icon className={`mx-auto mb-2 h-8 w-8 ${accent}`} />
            <p className="text-2xl font-bold text-zinc-900">{value}</p>
            <p className="mt-1 text-sm font-medium text-zinc-600">{label}</p>
          </Card>
        ))}
      </div>

      <Card className="mb-8" padding="p-5">
        <h3 className="mb-4 text-center text-lg font-bold text-zinc-900">
          {language === 'en' ? 'Prediction breakdown' : 'פירוט ניבויים'}
        </h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="text-center">
            <p className="text-xl font-bold text-emerald-600">{stats.perfectPredictions}</p>
            <p className="text-sm text-zinc-600">
              {language === 'en' ? 'Perfect scores' : 'ניבויים מושלמים'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-amber-600">{stats.correctResults}</p>
            <p className="text-sm text-zinc-600">
              {language === 'en' ? 'Correct results' : 'תוצאות נכונות'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-red-600">{stats.missedPredictions}</p>
            <p className="text-sm text-zinc-600">{language === 'en' ? 'Missed' : 'החטאות'}</p>
          </div>
        </div>
      </Card>

      <h3 className="mb-4 text-lg font-bold text-zinc-900">
        {language === 'en' ? 'All predictions' : 'כל הניבויים'}
      </h3>

      {userBets.length === 0 ? (
        <Card className="py-12 text-center">
          <User className="mx-auto mb-3 h-12 w-12 text-zinc-300" />
          <p className="font-medium text-zinc-700">
            {language === 'en' ? 'No predictions found' : 'לא נמצאו ניבויים'}
          </p>
          <p className="mt-1 text-sm text-zinc-500">
            {language === 'en'
              ? "This user hasn't made any predictions yet."
              : 'המשתמש עדיין לא ביצע ניבויים.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          <RoundFilterTabs
            language={language}
            view={betsView}
            onViewChange={handleViewChange}
            selectedRound={selectedRound}
            onRoundChange={setSelectedRound}
            rounds={rounds}
            allLabel={language === 'en' ? 'All results' : 'כל התוצאות'}
            selectId="profile-round-filter"
            ariaLabel={language === 'en' ? 'Prediction filters' : 'סינון ניבויים'}
          />
          {visibleBets.length === 0 ? (
            <Card className="py-12 text-center">
              <p className="font-medium text-zinc-700">
                {betsView === 'round' && !selectedRound
                  ? language === 'en'
                    ? 'Select a round to see predictions.'
                    : 'בחר סיבוב כדי לראות ניבויים.'
                  : language === 'en'
                    ? 'No predictions in this round.'
                    : 'אין ניבויים בסיבוב זה.'}
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {[...visibleBets]
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((bet) => {
              const finished = bet.gameId?.isFinished && bet.gameId?.finalResult;
              const points = finished
                ? calculatePoints(bet.predictedResult, bet.gameId.finalResult)
                : null;
              const tier =
                points === null
                  ? 'pending'
                  : points === 3
                    ? 'exact'
                    : points === 1
                      ? 'outcome'
                      : 'wrong';
              const { Icon, color, bg } = tierMeta[tier];

              return (
                <Card key={bet._id} className={`${bg} border`} padding="p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-zinc-900">
                        {bet.gameId?.homeGroupId?.logoUrl ? (
                          <img
                            src={bet.gameId.homeGroupId.logoUrl}
                            alt=""
                            className="h-6 w-6 rounded-full object-cover"
                          />
                        ) : null}
                        <span>{getTeamDisplayName(bet.gameId?.homeGroupId, language) || 'Home'}</span>
                        <span className="text-zinc-400">vs</span>
                        <span>{getTeamDisplayName(bet.gameId?.awayGroupId, language) || 'Away'}</span>
                        {bet.gameId?.awayGroupId?.logoUrl ? (
                          <img
                            src={bet.gameId.awayGroupId.logoUrl}
                            alt=""
                            className="h-6 w-6 rounded-full object-cover"
                          />
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-zinc-600">
                        <span>
                          {language === 'en' ? 'Prediction:' : 'ניבוי:'}{' '}
                          <ScoreText
                            score={bet.predictedResult}
                            className="font-semibold text-zinc-900"
                          />
                        </span>
                        {finished ? (
                          <span>
                            {language === 'en' ? 'Final:' : 'סופי:'}{' '}
                            <ScoreText
                              score={bet.gameId.finalResult}
                              className="font-semibold text-pitch-700"
                            />
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>{formatIsoDateTimeDMY(bet.createdAt)}</span>
                        {bet.gameId?.round ? (
                          <span className="rounded-md bg-brand-100 px-2 py-0.5 font-semibold text-brand-800">
                            {bet.gameId.round}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className={`flex shrink-0 flex-col items-center gap-1 ${color}`}>
                      <Icon className="h-6 w-6" />
                      {finished ? (
                        <span className="text-sm font-bold">
                          {points} {t('points')}
                        </span>
                      ) : (
                        <span className="text-xs font-semibold">
                          {language === 'en' ? 'Pending' : 'ממתין'}
                        </span>
                      )}
                      {user?.isAdmin && bet.gameId ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="mt-2"
                          onClick={() => setEditingBet(bet)}
                        >
                          <Pencil className="h-4 w-4" />
                          {language === 'en' ? 'Edit' : 'ערוך'}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </Card>
              );
            })}
            </div>
          )}
        </div>
      )}

      <div className="mt-8 flex justify-center">
        <Button variant="secondary" onClick={() => navigate(`${basePath}/leaderboard`)}>
          {language === 'en' ? 'Back to leaderboard' : 'חזור לטבלת מובילים'}
        </Button>
      </div>

      {editingBet && editingBet.gameId ? (
        <AdminBetScoreModal
          userId={userId}
          userName={userProfile.fullName || userProfile.username}
          game={editingBet.gameId}
          existingBet={editingBet}
          onClose={() => setEditingBet(null)}
          onSaved={() => {
            setEditingBet(null);
            fetchUserProfileAndBets();
          }}
        />
      ) : null}
    </PageContainer>
  );
};

export default UserProfile;
